import { skillLevelIds, skillLevelIdsOrder, skillLevels } from 'resources/skills/levels';
import { skillCategoryIds } from 'resources/skills/categories';

const imagesPath = './images';
const levelImagesPath = `${imagesPath}/levels`;

export const validatableIds = {
  // Batch-1 release ID-1424
  javascript: 'Javascript',
  java: 'Java',
  csharp: 'C#',
  python: 'Python',
  solidity: 'Solidity',
  php: 'Php',
  // Batch-2 Release
  c: 'c',
  cpp: 'C++',
  css: 'CSS',
  artificialIntelligence: 'artificial intelligence',
  sqlSever: 'sql-server',
  ruby: 'Ruby',
  r: 'r',
  // Batch-3 release
  hadoop: 'Hadoop',
  spring: 'spring',
  shell: 'shell',
  oop: 'oop',
  matlab: 'matlab',
  mongodb: 'mongodb',
  // Batch-4 release
  dataSciences: 'data sciences',
  legalFramework: 'legal framework',
  mysql: 'mysql',
  cryptographyAndNetworkSecurity: 'cryptography and network security',
  linux: 'linux',
  dataStructures: 'data-structures',
  // Batch-5 release
  html: 'HTML',
  // Batch-6 release ID-1677
  nodejs: 'node.js',
  reactjs: 'react.js',
  vuejs: 'vue.js',
  // next batch
  angularjs: 'angularjs',
  techincalWriting: 'Technical Writing',
  htmlcss: 'HTML/CSS',
  algorithm: 'algorithm',
  Java_android: 'Java_android',
  blockchain: 'Blockchain',
};

export const validatableIdsOrder = Object.keys(validatableIds).map(key => validatableIds[key]);

export const validationTypeIds = {
  aip: 'AIP',
  impressAI: 'impressAI',
};

export const AIPValidationEnabled = process.env.REACT_APP_AIP_ENABLED === 'true';
export const ImpressAIValidationEnabled = process.env.REACT_APP_IMPRESS_AI_ENABLED === 'true';
export const AtleastOneValidationEnabled = AIPValidationEnabled || ImpressAIValidationEnabled;

function AIPValidation(
  { levelIds = levelIdsOrder, disabled = !AIPValidationEnabled, AIPType = 'normal' } = {}
) {
  // ID-1386 AIPType can either be "normal" or "assignment-based". This will decide
  // whether we should show assignment lists for user to create NEW CLAIM
  return {
    type: validationTypeIds.aip,
    levelIds,
    disabled: !AIPValidationEnabled || disabled,
    AIPType,
  };
}

const disabledAIP = AIPValidation({ disabled: true });

function ImpressAIValidation({ badgePrettyId, disabled = !ImpressAIValidationEnabled }) {
  return {
    type: validationTypeIds.impressAI,
    badgePrettyId,
    disabled: !ImpressAIValidationEnabled || disabled,
  };
}

export const levelIds = skillLevelIds;

export const levelIdsOrder = skillLevelIdsOrder;

function getIconsByLevel({ baseName, path = levelImagesPath, levelIds = levelIdsOrder }) {
  const result = [];
  levelIds.forEach(level => {
    result[level] = require(`${path}/${[baseName, level].filter(Boolean).join('-')}.png`);
  });
  return result;
}

export const validatables = {
  [validatableIds.javascript]: {
    name: 'JavaScript',
    icon: require(`${imagesPath}/js.png`),
    iconsByLevel: getIconsByLevel({ baseName: 'js' }),
    description:
      'JavaScript (JS) is a lightweight, interpreted, programming language with first-class functions.',
    links: [
      {
        name: 'Mozilla Docs',
        url: 'https://developer.mozilla.org/bm/docs/Web/JavaScript',
      },
    ],
    skill: {
      name: 'javascript',
      category: skillCategoryIds.technology,
    },
    validations: [
      AIPValidation(),
      ImpressAIValidation({
        badgePrettyId: 'javascript_beginner',
      }),
    ],
  },
  [validatableIds.Java_android]: {
    name: 'Java Android',
    icon: null,
    iconsByLevel: getIconsByLevel({ baseName: 'js' }),
    description: 'Java for android',
    links: [
      {
        name: 'Mozilla Docs',
        url: 'https://developer.mozilla.org/bm/docs/Web/JavaScript',
      },
    ],
    skill: {
      name: 'Java_android',
      category: skillCategoryIds.technology,
    },
    validations: [AIPValidation()],
  },
  [validatableIds.blockchain]: {
    name: 'Blockchain',
    icon: null,
    iconsByLevel: getIconsByLevel({ baseName: 'js' }),
    description: 'Blockchain',
    links: [
      {
        name: 'Mozilla Docs',
        url: 'https://developer.mozilla.org/bm/docs/Web/JavaScript',
      },
    ],
    skill: {
      name: 'blockchain',
      category: skillCategoryIds.technology,
    },
    validations: [AIPValidation()],
  },
  [validatableIds.java]: {
    name: 'Java',
    icon: require(`${imagesPath}/java.png`),
    iconsByLevel: getIconsByLevel({ baseName: 'java' }),
    description:
      'Java is a general-purpose computer-programming language that is concurrent, class-based, object-oriented, and specifically designed to have as few implementation dependencies as possible. It is intended to let application developers "write once, run anywhere" (WORA), meaning that compiled Java code can run on all platforms that support Java without the need for recompilation.',
    links: [
      {
        name: 'Official Website',
        url: 'https://www.java.com/',
      },
    ],
    skill: {
      name: 'java',
      category: skillCategoryIds.technology,
    },
    validations: [
      // ID-1386 enable Java for assignment-based AIP validation
      AIPValidation({ AIPType: 'assignment-based' }),
      ImpressAIValidation({
        badgePrettyId: 'java_beginner',
      }),
    ],
  },
  [validatableIds.csharp]: {
    name: 'C#',
    icon: require(`${imagesPath}/c-sharp.png`),
    iconsByLevel: getIconsByLevel({ baseName: 'c-sharp' }),
    description:
      'C# is a multi-paradigm programming language encompassing strong typing, imperative, declarative, functional, generic, object-oriented (class-based), and component-oriented programming disciplines',
    links: [
      {
        name: 'Official Website',
        url: 'https://docs.microsoft.com/en-us/dotnet/csharp',
      },
    ],
    skill: {
      name: 'csharp',
      category: skillCategoryIds.technology,
    },
    validations: [
      AIPValidation(),
      ImpressAIValidation({
        badgePrettyId: 'csharp_beginner',
      }),
    ],
  },
  [validatableIds.python]: {
    name: 'Python',
    icon: require(`${imagesPath}/python.png`),
    iconsByLevel: getIconsByLevel({ baseName: 'python' }),
    description:
      'Python is a programming language that lets you work quickly and integrate systems more effectively.',
    links: [
      {
        name: 'Official Website',
        url: 'https://www.python.org/',
      },
    ],
    skill: {
      name: 'python',
      category: skillCategoryIds.technology,
    },
    validations: [
      AIPValidation(),
      ImpressAIValidation({
        badgePrettyId: 'python_beginner',
      }),
    ],
  },
  // [validatableIds.solidity]: {
  //   name: 'Solidity',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'Solidity is a contract-oriented, high-level language whose syntax is similar to that of JavaScript and it is designed to target the Ethereum Virtual Machine (EVM).',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://solidity.readthedocs.io/en/develop/',
  //     },
  //   ],
  //   skill: {
  //     name: 'solidity',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'solidity_beginner',
  //     }),
  //   ],
  // },
  [validatableIds.php]: {
    name: 'Php',
    icon: require(`${imagesPath}/php.png`),
    iconsByLevel: getIconsByLevel({ baseName: 'php' }),
    description:
      'Hypertext Preprocessor is a server-side scripting language designed for Web development, but also used as a general-purpose programming language',
    links: [
      {
        name: 'Official Website',
        url: 'https://secure.php.net/',
      },
    ],
    skill: {
      name: 'php',
      category: skillCategoryIds.technology,
    },
    validations: [
      AIPValidation(),
      ImpressAIValidation({
        badgePrettyId: 'php_beginner',
      }),
    ],
  },
  // [validatableIds.c]: {
  //   name: 'C',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   description:
  //     'C is a general-purpose, imperative computer programming language, supporting structured programming, lexical variable scope and recursion, while a static type system prevents many unintended operations',
  //   links: [
  //     {
  //       name: 'The C Programming Language',
  //       url: 'https://en.wikipedia.org/wiki/C_(programming_language)',
  //     },
  //   ],
  //   skill: {
  //     name: 'c',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'c_beginner',
  //     }),
  //   ],
  // },
  [validatableIds.cpp]: {
    name: 'C++',
    icon: require(`${imagesPath}/cpp.png`),
    description:
      'C++ is a general-purpose programming language. It has imperative, object-oriented and generic programming features, while also providing facilities for low-level memory manipulation.',
    links: [
      {
        name: 'Standard C++',
        url: 'https://isocpp.org/',
      },
    ],
    skill: {
      name: 'cpp',
      category: skillCategoryIds.technology,
    },
    validations: [
      AIPValidation(),
      ImpressAIValidation({
        badgePrettyId: 'cpp_beginner',
      }),
    ],
  },
  // [validatableIds.css]: {
  //   name: 'CSS',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'Cascading Style Sheets (CSS) is a style sheet language used for describing the presentation of a document written in a markup language like HTML',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://www.w3.org/TR/CSS/',
  //     },
  //   ],
  //   skill: {
  //     name: 'css',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'css_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.artificialIntelligence]: {
  //   name: 'Artificial Intelligence',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'Artificial intelligence (AI), sometimes called machine intelligence, is intelligence demonstrated by machines, in contrast to the natural intelligence displayed by humans and other animals',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://en.wikipedia.org/wiki/Artificial_intelligence',
  //     },
  //   ],
  //   skill: {
  //     name: 'artificial intelligence',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'artificialIntelligence_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.sqlSever]: {
  //   name: 'SQL-Server',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'Microsoft SQL Server is a relational database management system developed by Microsoft',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'www.microsoft.com/en-us/sql-server',
  //     },
  //   ],
  //   skill: {
  //     name: 'sql-server',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'sqlSever_beginner',
  //     }),
  //   ],
  // },
  [validatableIds.ruby]: {
    name: 'Ruby',
    icon: require(`${imagesPath}/solidity.png`),
    iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
    description:
      'Ruby is a dynamic, interpreted, reflective, object-oriented, general-purpose programming language',
    links: [
      {
        name: 'Official Website',
        url: 'www.ruby-lang.org',
      },
    ],
    skill: {
      name: 'ruby',
      category: skillCategoryIds.technology,
    },
    validations: [
      AIPValidation(),
      ImpressAIValidation({
        badgePrettyId: 'ruby_beginner',
      }),
    ],
  },
  // [validatableIds.r]: {
  //   name: 'R',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'R is a programming language and free software environment for statistical computing and graphics supported by the R Foundation for Statistical Computing.',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://www.r-project.org/',
  //     },
  //   ],
  //   skill: {
  //     name: 'r',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'r_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.hadoop]: {
  //   name: 'Hadoop',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'It provides a software framework for distributed storage and processing of big data using the MapReduce programming model',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://hadoop.apache.org/',
  //     },
  //   ],
  //   skill: {
  //     name: 'hadoop',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'hadoop_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.spring]: {
  //   name: 'Spring',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'The Spring Framework is an application framework and inversion of control container for the Java platform',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'spring.io',
  //     },
  //   ],
  //   skill: {
  //     name: 'spring',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'spring_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.shell]: {
  //   name: 'Shell',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'A shell script is a computer program designed to be run by the Unix shell, a command-line interpreter',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://en.wikipedia.org/wiki/Unix_shell',
  //     },
  //   ],
  //   skill: {
  //     name: 'shell',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'shell_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.oop]: {
  //   name: 'OOP',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'In computer science, a data structure is a data organization, management and storage format that enables efficient access and modification',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://en.wikipedia.org/wiki/Data_structure',
  //     },
  //   ],
  //   skill: {
  //     name: 'oop',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'oop_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.matlab]: {
  //   name: 'MATLAB',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'MATLAB is a high-level language that includes mathematical functions for solving engineering and scientific problems',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'mathworks.com',
  //     },
  //   ],
  //   skill: {
  //     name: 'matlab',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'matlab_beginner',
  //     }),
  //   ],
  // },
  [validatableIds.dataSciences]: {
    name: 'Data Sciences',
    icon: require(`${imagesPath}/solidity.png`),
    iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
    description:
      'Data science is an interdisciplinary field that uses scientific methods, processes, algorithms and systems to extract knowledge and insights from data in various forms, both structured and unstructured',
    links: [
      {
        name: 'Official Website',
        url: 'https://en.wikipedia.org/wiki/Data_science',
      },
    ],
    skill: {
      name: 'data science',
      category: skillCategoryIds.technology,
    },
    validations: [
      disabledAIP,
      ImpressAIValidation({
        badgePrettyId: 'dataSciences_beginner',
      }),
    ],
  },
  // [validatableIds.legalFramework]: {
  //   name: 'Legal Framework',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description: 'Legal Framework',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://en.wikipedia.org/wiki/Data_science',
  //     },
  //   ],
  //   skill: {
  //     name: 'legal framework',
  //     category: skillCategoryIds.business,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'legalFramework_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.mysql]: {
  //   name: 'MySQL',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     "MySQL is an open-source relational database management system. Its name is a combination of 'My', the name of co-founder Michael Widenius's daughter, and 'SQL', the abbreviation for Structured Query Language",
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'MySQL.com',
  //     },
  //   ],
  //   skill: {
  //     name: 'mysql',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'mysql_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.cryptographyAndNetworkSecurity]: {
  //   name: 'Cryptography And Network Security',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'Cryptography is about constructing and analyzing protocols that prevent third parties or the public from reading private messages',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://en.wikipedia.org/wiki/Cryptography',
  //     },
  //   ],
  //   skill: {
  //     name: 'cryptography and network security',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     // ImpressAIValidation({
  //     //   badgePrettyId: 'cryptographyAndNetworkSecurity_beginner',
  //     // }),
  //   ],
  // },
  // [validatableIds.linux]: {
  //   name: 'Linux',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'Linux is a family of free and open-source software operating systems built around the Linux kernel',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'www.kernel.org',
  //     },
  //   ],
  //   skill: {
  //     name: 'linux',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'linux_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.dataStructures]: {
  //   name: 'Data Structures',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'In computer science, a data structure is a data organization, management and storage format that enables efficient access and modification',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://en.wikipedia.org/wiki/Data_structure',
  //     },
  //   ],
  //   skill: {
  //     name: 'data-structures',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'dataStructures_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.html]: {
  //   name: 'HTML',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'Hypertext Markup Language (HTML) is the standard markup language for creating web pages and web applications',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'www.w3.org/html/',
  //     },
  //   ],
  //   skill: {
  //     name: 'html',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     // ImpressAIValidation({
  //     //   badgePrettyId: 'html_beginner',
  //     // }),
  //   ],
  // },

  // [validatableIds.mongodb]: {
  //   name: 'mongoDB',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description:
  //     'MongoDB is a free and open-source cross-platform document-oriented database program',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'www.mongodb.com',
  //     },
  //   ],
  //   skill: {
  //     name: 'mongodb',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'mongodb_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.nodejs]: {
  //   name: 'NodeJS',
  //   icon: require(`${imagesPath}/node-js.png`),
  //   description:
  //     "Node.js® is a JavaScript runtime built on Chrome's V8 JavaScript engine. Node.js uses an event-driven, non-blocking I/O model that makes it lightweight and efficient. Node.js' package ecosystem, npm, is the largest ecosystem of open source libraries in the world.",
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://nodejs.org',
  //     },
  //   ],
  //   skill: {
  //     name: 'node.js',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'node_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.reactjs]: {
  //   name: 'ReactJS',
  //   icon: require(`${imagesPath}/react-js.png`),
  //   description:
  //     'A declarative, efficient, and flexible JavaScript library for building user interfaces.',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://facebook.github.io/react/',
  //     },
  //   ],
  //   skill: {
  //     name: 'react.js',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'react_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.vuejs]: {
  //   name: 'VueJS',
  //   icon: require(`${imagesPath}/vue-js.png`),
  //   description:
  //     'A progressive, incrementally-adoptable JavaScript framework for building UI on the web',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'http://vuejs.org',
  //     },
  //   ],
  //   skill: {
  //     name: 'vue.js',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     ImpressAIValidation({
  //       badgePrettyId: 'vue_beginner',
  //     }),
  //   ],
  // },
  // [validatableIds.angularjs]: {
  //   name: 'AngularJS',
  //   icon: require(`${imagesPath}/angular-js.png`),
  //   description:
  //     'Angular.js is a JavaScript-based open-source front-end web application framework ',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://angular.io/',
  //     },
  //   ],
  //   skill: {
  //     name: 'angularjs',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [disabledAIP],
  // },
  // [validatableIds.techincalWriting]: {
  //   name: 'Technical Writing',
  //   icon: require(`${imagesPath}/tech-writing.png`),
  //   description:
  //     'Technical writing is any written form of writing or drafting technical communication used in a variety of technical and occupational fields, such as computer hardware and software, engineering, chemistry, aeronautics, robotics, finance, medical, consumer electronics, and biotechnology.',
  //   links: [
  //     {
  //       name: 'Wiki',
  //       url: 'https://wikipedia.org/wiki/Technical_writing',
  //     },
  //   ],
  //   validations: [disabledAIP],
  // },
  // [validatableIds.htmlcss]: {
  //   name: 'HTML/CSS',
  //   icon: require(`${imagesPath}/html-css.png`),
  //   description: [
  //     'HTML is the standard markup language used to create web pages and its elements form the building blocks of all websites.',
  //     'Cascading Style Sheets (CSS) is a simple mechanism for adding style (e.g., fonts, colors, spacing) to Web documents.',
  //   ],
  //   links: [
  //     {
  //       name: 'HTML Official Website',
  //       url: 'https://www.w3.org/html/',
  //     },
  //     {
  //       name: 'CSS Official Website',
  //       url: 'https://www.w3.org/Style/CSS/Overview.en.html',
  //     },
  //   ],
  //   validations: [disabledAIP],
  // },
  // [validatableIds.algorithm]: {
  //   name: 'Algorithm',
  //   icon: require(`${imagesPath}/solidity.png`),
  //   iconsByLevel: getIconsByLevel({ baseName: 'solidity' }),
  //   description: 'Algorithm is an unambiguous specification of how to solve a class of problems',
  //   links: [
  //     {
  //       name: 'Official Website',
  //       url: 'https://en.wikipedia.org/wiki/Algorithm',
  //     },
  //   ],
  //   skill: {
  //     name: 'algorithm',
  //     category: skillCategoryIds.technology,
  //   },
  //   validations: [
  //     disabledAIP,
  //     // ImpressAIValidation({
  //     //   badgePrettyId: 'algorithm_beginner',
  //     // }),
  //   ],
  // },
};

Object.keys(validatables).forEach(validatableId => {
  const validatable = validatables[validatableId];

  validatable.id = validatableId;
});

export const levels = skillLevels;

export function isValidationAllowedForRole(validation, role) {
  return true;
  // if (['admin', 'full_access'].indexOf(role) >= 0) return true;
  // if (role === undefined && validation && validation.type === validationTypeIds.aip) return true;

  // return role !== 'no_access' && validation && validation.type !== validationTypeIds.aip;
}

export function findValidatableBySkillName(skillName, aipSkills) {
  aipSkills =
    aipSkills && typeof aipSkills.asMutable !== 'undefined' ? aipSkills.asMutable() : aipSkills;
  const filteredSkills =
    aipSkills &&
    aipSkills.filter(aip => {
      return aip.name && skillName && aip.name.toLowerCase() === skillName.toLowerCase();
    });
  return filteredSkills && filteredSkills.length > 0 && filteredSkills[0];
}

export function isSkillValidatable(skillName, aipSkills) {
  return findValidatableBySkillName(skillName, aipSkills); // if skill exists in global AIP list it is validatable
}

export function isAIPEnabled(skillName, aipSkills) {
  return isSkillValidatable(skillName, aipSkills);
}

export function isChatbotEnabled(skillName, aipSkills) {
  return isSkillValidatable(skillName, aipSkills);
}
